import React,{useState,useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {supabase} from '../../supabaseClient';
import {PlanHeader} from '../../components/owner/PlanFeature';
import TaskListView from '../../components/owner/TaskListView';
import TaskCalendarView from '../../components/owner/TaskCalendarView';
import OwnerChat from '../../components/owner/OwnerChat';
import PlanNotes from '../../components/common/PlanNotes';
import ResourceLibrary from '../../components/owner/ResourceLibrary';
import SafeIcon from '../../common/SafeIcon';

const OwnerPlanView=()=> {
  const {planId}=useParams();
  const [plan,setPlan]=useState(null);
  const [client,setClient]=useState(null);
  const [tasks,setTasks]=useState([]);
  const [loading,setLoading]=useState(true);
  const [viewMode,setViewMode]=useState('list');
  const [activeTab,setActiveTab]=useState('plan');

  const fetchData=async ()=> {
    const {data: clientData}=await supabase.from('training_clients').select('*').eq('id',planId).maybeSingle();
    if (!clientData) {setLoading(false);return;}
    setClient(clientData);

    const {data: plans}=await supabase
      .from('training_plans')
      .select('*')
      .eq('client_id',planId)
      .order('start_date',{ascending: false});
    
    if (!plans?.length) {setLoading(false);return;}
    const selected=plans[0];
    setPlan(selected);

    const {data: tasksData}=await supabase
      .from('training_tasks')
      .select('*')
      .eq('plan_id',selected.id)
      .order('week_number')
      .order('day_number');
    setTasks(tasksData || []);
    setLoading(false);
  };

  useEffect(()=> {
    if (planId) fetchData();
  },[planId]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-pulse flex flex-col items-center">
        <div className="w-12 h-12 bg-indigo-600 rounded-full animate-bounce mb-4" />
        <p className="text-gray-400 font-black uppercase tracking-widest text-xs">Loading Plan...</p>
      </div>
    </div>
  );

  if (plan && !plan.is_paid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white p-12 rounded-[3.5rem] shadow-2xl border border-gray-100 text-center">
          <div className="w-20 h-20 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon name="Clock" className="w-10 h-10" />
          </div>
          <h2 className="text-3xl font-black text-gray-900 mb-2">Plan Locked</h2>
          <p className="text-gray-500 font-medium mb-8">
            Your training plan for <b>{client?.dog_name}</b> is currently locked until payment is confirmed.
          </p>
          <a href={plan.payment_link} target="_blank" rel="noopener noreferrer" className="w-full bg-indigo-600 text-white py-5 rounded-2xl font-black shadow-xl flex items-center justify-center gap-3">
            Pay to Unlock <SafeIcon name="ExternalLink" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PlanHeader plan={plan} client={client} onUpdate={fetchData} />
      <div className="max-w-4xl mx-auto px-2 sm:px-4 mt-6">
        <div className="flex gap-6 border-b mb-6 overflow-x-auto whitespace-nowrap scrollbar-hide">
          <button onClick={()=> setActiveTab('plan')} className={`pb-3 font-bold border-b-2 transition-all ${activeTab==='plan' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400'}`}>Training Plan</button>
          <button onClick={()=> setActiveTab('library')} className={`pb-3 font-bold border-b-2 transition-all ${activeTab==='library' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400'}`}>Support Library</button>
          <button onClick={()=> setActiveTab('notes')} className={`pb-3 font-bold border-b-2 transition-all ${activeTab==='notes' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400'}`}>Notes & Journal</button>
          <button onClick={()=> setActiveTab('messages')} className={`pb-3 font-bold border-b-2 transition-all ${activeTab==='messages' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400'}`}>Messages</button>
        </div>

        {activeTab==='plan' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="bg-white p-1 rounded-xl border border-gray-200 inline-flex shadow-sm">
                <button onClick={()=> setViewMode('list')} className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${viewMode==='list' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-500'}`}>List View</button>
                <button onClick={()=> setViewMode('calendar')} className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${viewMode==='calendar' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-500'}`}>Calendar</button>
              </div>
            </div>
            {viewMode==='list' ? (
              <TaskListView tasks={tasks} onUpdate={fetchData} planStartDate={plan?.start_date} />
            ) : (
              <TaskCalendarView tasks={tasks} onUpdate={fetchData} planStartDate={plan?.start_date} />
            )}
          </>
        )}

        {activeTab==='library' && client && <ResourceLibrary trainerId={client.trainer_id} />}
        {activeTab==='notes' && <PlanNotes planId={plan.id} userRole="owner" />}
        {activeTab==='messages' && <OwnerChat planId={plan.id} client={client} />}
      </div>
    </div>
  );
};

export default OwnerPlanView;